import { ACCESS_TOKEN, REDIRECT_FLOW, REFRESH_TOKEN, CHECKED_LIST, LOGIN } from "../constants";
import CrossOriginLocalStorage from "../utils/CrossOriginLocalStorage";
import { jwtDecode } from "jwt-decode";
import { dispatch } from "../utils/Extension";
import logger from "../utils/logger";
import ApiService from "./api.service";

type JWTPayload = {
  sub: string;
  exp: number;
  [key: string]: any;
};

type User = {
  [key: string]: any;
};

// Monitor localStorage for token removals (including external/extension clears)
let localStorageMonitorInitialized = false;

// Track token state across page loads using sessionStorage (survives reloads but not tab close)
const TOKEN_STATE_KEY = '__token_state_tracker';
const TOKEN_PRESENT_KEY = '__token_was_present';

/**
 * Persistent token event log — survives page navigation (stored in sessionStorage).
 * Events are appended so we can see the full timeline even after redirect to /login.
 * Call AuthService.getTokenEventLog() on the login page to retrieve the log.
 */
const TOKEN_EVENT_LOG_KEY = '__token_event_log';
function logTokenEvent(event: string, data?: Record<string, any>): void {
  try {
    const log = JSON.parse(sessionStorage.getItem(TOKEN_EVENT_LOG_KEY) || '[]') as any[];
    // Keep last 30 events to avoid storage bloat
    if (log.length >= 30) log.shift();
    log.push({
      event,
      time: new Date().toISOString(),
      ...data,
    });
    sessionStorage.setItem(TOKEN_EVENT_LOG_KEY, JSON.stringify(log));
  } catch { /* sessionStorage full or unavailable */ }
}

function initializeLocalStorageMonitor(): void {
  if (localStorageMonitorInitialized) return;
  localStorageMonitorInitialized = true;

  // === STARTUP TOKEN DIAGNOSTIC ===
  const hasAccessToken = localStorage.getItem(ACCESS_TOKEN) !== null;
  const hasRefreshToken = localStorage.getItem(REFRESH_TOKEN) !== null;
  const tokenWasPresent = sessionStorage.getItem(TOKEN_PRESENT_KEY) === 'true';

  logger.info('AUTH', '🔑 Startup token diagnostic', {
    hasAccessToken,
    hasRefreshToken,
    tokenWasPresent,
    pathname: window.location.pathname
  });

  logTokenEvent('STARTUP', { hasAccessToken, hasRefreshToken, pathname: window.location.pathname });

  if (hasAccessToken && !hasRefreshToken) {
    console.warn(
      '%c[AUTH WARNING] Access token exists but NO refresh token! Session will expire without renewal.',
      'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;'
    );
    logger.error('AUTH', '⚠️ Access token present but refresh token MISSING at startup — logout is inevitable when token expires');
    logTokenEvent('⚠️ NO_REFRESH_AT_STARTUP', { pathname: window.location.pathname });
  }

  if (tokenWasPresent && !hasAccessToken) {
    logger.error('AUTH', 'Token disappeared between checks');
  }

  // Update state
  sessionStorage.setItem(TOKEN_PRESENT_KEY, hasAccessToken ? 'true' : 'false');
  if (!sessionStorage.getItem('__session_start')) {
    sessionStorage.setItem('__session_start', Date.now().toString());
  }

  // Monitor storage events (cross-tab/window changes)
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === ACCESS_TOKEN || e.key === REFRESH_TOKEN) {
      const action = e.newValue === null ? 'REMOVED' : (e.oldValue === null ? 'ADDED' : 'CHANGED');
      const source = e.storageArea === localStorage ? 'localStorage' : 'sessionStorage';
      const fromExternal = e.storageArea !== localStorage; // StorageEvent fires for cross-tab/window changes
      
      logger.warn('AUTH', 'Token storage event', { key: e.key, action });
    }
  });

  // Monitor direct localStorage.removeItem() calls by intercepting
  const originalRemoveItem = Storage.prototype.removeItem;
  Storage.prototype.removeItem = function(key: string) {
    if (key === ACCESS_TOKEN || key === REFRESH_TOKEN) {
      const hadToken = this.getItem(key) !== null;
      sessionStorage.setItem(TOKEN_PRESENT_KEY, 'false');
      
      logger.error('AUTH', 'Token removal detected', { key, hadToken });
    }
    return originalRemoveItem.call(this, key);
  };

  // Monitor localStorage.clear() calls
  const originalClear = Storage.prototype.clear;
  Storage.prototype.clear = function() {
    const hadAccessToken = this.getItem(ACCESS_TOKEN) !== null;
    const hadRefreshToken = this.getItem(REFRESH_TOKEN) !== null;
    
    if (hadAccessToken || hadRefreshToken) {
      sessionStorage.setItem(TOKEN_PRESENT_KEY, 'false');
      logger.error('AUTH', 'Tokens cleared by localStorage.clear()');
    }
    return originalClear.call(this);
  };

}

export class AuthService {
  static get token(): string | null {
    // Initialize monitor on first access
    if (typeof window !== 'undefined') {
      initializeLocalStorageMonitor();
    }
    
    const token = localStorage.getItem(ACCESS_TOKEN);

    // Handle legacy JSON format: {"access_token":"...","refresh_token":"..."}
    if (token && token.startsWith('{')) {
      try {
        const tokenObj = JSON.parse(token);
        if (tokenObj.access_token) {
          logger.warn('AUTH', 'Migrating legacy JSON token format to string format');
          // Migrate to new format
          localStorage.setItem(ACCESS_TOKEN, tokenObj.access_token);
          if (tokenObj.refresh_token) {
            localStorage.setItem('refreshToken', tokenObj.refresh_token);
          }
          return tokenObj.access_token;
        }
      } catch (e) {
        logger.error('AUTH', 'Failed to parse legacy token format', e);
      }
    }

    return token;
  }

  static set token(token: string | null) {
    if (!token) {
      logger.warn('AUTH', 'Attempted to set empty token');
      return;
    }
    const localToken = AuthService.token;
    if (localToken) {
      try {
        const newTokenExp = jwtDecode<JWTPayload>(token).exp;
        const localTokenExp = jwtDecode<JWTPayload>(localToken).exp;
        const isFreshToken = newTokenExp >= localTokenExp;
        // ⚠️ FIX: If the stored token is already expired, accept the new token
        // regardless of strict freshness. The original `>=` check was intended to
        // prevent multi-tab downgrade (an in-flight slow refresh installing an
        // older token over a fresher one from another tab). But when the stored
        // token is dead, "downgrade" is meaningless — keeping it traps the user
        // in an infinite getRefreshToken → setter-reject → loop guard cycle.
        // The server can legitimately issue earlier-exp tokens (clock skew, or
        // access-token TTL bounded by refresh-token's remaining life).
        const oldIsExpired = localTokenExp * 1000 <= Date.now();
        if (isFreshToken || oldIsExpired) {
          localStorage.setItem(ACCESS_TOKEN, token);
          // Clear FORCE_LOGOUT flag on successful token refresh
          this.clearForceLogoutFlag();
        } else {
          // 📊 OBSERVABILITY: log rejections so this silent failure mode is
          // catchable next time. Previously the setter discarded without trace.
          logger.warn('AUTH', 'Token setter rejected new token — stored token is newer and still valid', {
            newExp: newTokenExp,
            localExp: localTokenExp,
            newExpiresInMs: newTokenExp * 1000 - Date.now(),
            localExpiresInMs: localTokenExp * 1000 - Date.now()
          });
        }
      } catch (error) {
        // Decode failed for old or new token — store the new one anyway since
        // the server just issued it. Keeping a corrupt old token is worse.
        logger.error('AUTH', 'Token decode error during comparison — storing new token', error);
        localStorage.setItem(ACCESS_TOKEN, token);
        this.clearForceLogoutFlag();
      }
    } else {
      localStorage.setItem(ACCESS_TOKEN, token);
      localStorage.removeItem(CHECKED_LIST);
      // Clear FORCE_LOGOUT flag on successful login/new token
      this.clearForceLogoutFlag();
      // Update tracking
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(TOKEN_PRESENT_KEY, 'true');
      }
    }
  }

  static get refreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN);
  }

  static set refreshToken(token: string | null) {
    if (!token) {
      logTokenEvent('REFRESH_SET_SKIPPED', { reason: 'token is null/empty' });
      return;
    }
    const localToken = AuthService.refreshToken;
    if (localToken) {
      try {
        const isFreshToken = jwtDecode<JWTPayload>(token).exp >= jwtDecode<JWTPayload>(localToken).exp;
        if (isFreshToken) {
          localStorage.setItem(REFRESH_TOKEN, token);
          logTokenEvent('REFRESH_STORED', { source: 'setter_update' });
        } else {
          logTokenEvent('REFRESH_SET_SKIPPED', { reason: 'new token older than existing' });
        }
      } catch (e) {
        // If decode fails, store the new token anyway — the server just issued it
        logger.error('AUTH', 'Failed to decode refresh token for comparison — storing new token', e);
        localStorage.setItem(REFRESH_TOKEN, token);
        logTokenEvent('REFRESH_STORED', { source: 'setter_decode_fallback' });
      }
    } else {
      localStorage.setItem(REFRESH_TOKEN, token);
      logTokenEvent('REFRESH_STORED', { source: 'setter_first_set' });
    }
  }

  static get user(): User | null {
    const token = AuthService.token;
    if (!token) return null;

    let user: User | null = null;
    try {
      const decodedToken = jwtDecode<JWTPayload>(token);
      if (decodedToken && decodedToken.sub) {
        user = JSON.parse(decodedToken.sub);
      }
    } catch (error) {
      logger.error('AUTH', 'Error decoding user from token', error);
    }
    return user;
  }

  /**
   * Record the reason for logout so the login page can display it.
   * Stored in sessionStorage (survives redirect, cleared on tab close).
   */
  static setLogoutReason(reason: string): void {
    try {
      // Capture a short stack trace to identify which code path triggered the logout
      const stack = new Error().stack?.split('\n').slice(1, 5).map(s => s.trim()).join(' | ') || '';
      const info = {
        reason,
        timestamp: new Date().toISOString(),
        pathname: window.location.pathname,
        stack,
        hadAccessToken: !!localStorage.getItem(ACCESS_TOKEN),
        hadRefreshToken: !!localStorage.getItem(REFRESH_TOKEN),
      };
      sessionStorage.setItem('__logout_reason', JSON.stringify(info));
      logTokenEvent('LOGOUT_REASON', { reason, hadAccessToken: info.hadAccessToken, hadRefreshToken: info.hadRefreshToken, pathname: info.pathname });

      // Visible console output so it's obvious in DevTools
      console.error(
        '%c[AUTH] LOGOUT REASON SET',
        'background: #a00; color: #fff; font-size: 14px; padding: 4px 8px;',
        info
      );
    } catch {
      // sessionStorage may be unavailable in some contexts
    }
  }

  /**
   * Retrieve and clear the logout reason (call on login page mount).
   */
  static getAndClearLogoutReason(): { reason: string; timestamp: string; pathname: string } | null {
    try {
      const raw = sessionStorage.getItem('__logout_reason');
      sessionStorage.removeItem('__logout_reason');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  static logout(): void {
    logger.warn('AUTH', 'Logout called', { hasToken: !!AuthService.token });

    // Stop auto-logout monitoring (safe to call even if already stopped via globalLogout)
    try {
      const AutoLogoutModule = require('./autologout.service').default;
      const autoLogoutInstance = AutoLogoutModule.getInstance();
      if (autoLogoutInstance) {
        autoLogoutInstance.stop();
      }
    } catch (e) {
      // Failed to stop auto-logout service
    }

    const token = AuthService.token;

    const hadAccessToken = !!localStorage.getItem(ACCESS_TOKEN);
    const hadRefreshToken = !!localStorage.getItem(REFRESH_TOKEN);

    sessionStorage.removeItem("agentConnectedOnce");
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(REDIRECT_FLOW);
    localStorage.removeItem(CHECKED_LIST);
    localStorage.removeItem(LOGIN);
    // Clear cached user data
    localStorage.removeItem("cached_current_user");
    localStorage.removeItem("cached_current_user_timestamp");
    
    logger.info('AUTH', 'Tokens removed in logout');

    // Clear cached tree state and UI data to prevent stale data on re-login
    localStorage.removeItem("treeState");
    localStorage.removeItem("selectedBranch");
    localStorage.removeItem("expandedNodes");
    localStorage.removeItem("lastSelectedProject");
    sessionStorage.removeItem("persistAllState");
    
    // Note: FORCE_LOGOUT flag is NOT removed here - it's removed on successful login/token refresh
    // This ensures we can track if a logout occurred between sessions

    if (!token) {
      logger.warn('AUTH', 'Logout called but no token found');
      logger.warn('AUTH', 'Logout called with no token, redirecting');
      // Still redirect even if no token
      window.location.href = "/login";
      return;
    }
    // Disable recording when user logs out to prevent extension from monitoring
    try {
      const storedValue = localStorage.getItem("default_ext_values");
      if (storedValue) {
        const parsed = JSON.parse(storedValue);
        parsed.isRecording = false;
        localStorage.setItem("default_ext_values", JSON.stringify(parsed));
      }
    } catch (e) {
      // Failed to disable recording
    }

    // Notify browser extension that user logged out
    dispatch({
      type: "logout",
      value: "User logged out - extension should stop monitoring"
    });

    window.location.href = "/login";
  }

  /**
   * Clear the FORCE_LOGOUT flag - called after successful authentication
   */
  static clearForceLogoutFlag(): void {
    const hadFlag = localStorage.getItem('FORCE_LOGOUT') !== null;
    if (hadFlag) {
      localStorage.removeItem('FORCE_LOGOUT');
    }
  }

  static globalLogout(): void {
    const stack = new Error().stack?.split('\n').slice(1, 6).map(s => s.trim()).join('\n') || '';
    const info = {
      hasToken: !!AuthService.token,
      hasRefreshToken: !!AuthService.refreshToken,
      pathname: window.location.pathname,
      time: new Date().toISOString(),
    };
    console.error(
      '%c[AUTH] 🚨 GLOBAL LOGOUT CALLED',
      'background: #a00; color: #fff; font-size: 16px; padding: 6px 10px;',
      info,
      '\nStack:', stack
    );
    logger.error('AUTH', 'Global logout initiated', info);

    // Stop auto-logout monitoring
    try {
      const AutoLogout = require('./autologout.service').default;
      const autoLogoutInstance = AutoLogout.getInstance();
      if (autoLogoutInstance) {
        autoLogoutInstance.stop();
      }
    } catch (e) {
      // Failed to stop auto-logout service
    }

    // Clean up activity listeners for token refresh
    try {
      const apiService = ApiService.getInstance();
      apiService.cleanupActivityListeners();
    } catch (e) {
      // Failed to cleanup activity listeners
    }

    const authIframe = document.getElementById("auth-ui-frame") as HTMLIFrameElement | null;
    if (authIframe) {
      const crossStorage = new CrossOriginLocalStorage(authIframe);
      crossStorage.removeData(ACCESS_TOKEN);
      crossStorage.removeData(REFRESH_TOKEN);
    }
    sessionStorage.removeItem("agentConnectedOnce");
    localStorage.removeItem(CHECKED_LIST);
    localStorage.removeItem(LOGIN);

    // Clear cached tree state and UI data
    localStorage.removeItem("treeState");
    localStorage.removeItem("selectedBranch");
    localStorage.removeItem("expandedNodes");
    localStorage.removeItem("lastSelectedProject");
    sessionStorage.removeItem("persistAllState");

    const forceLogoutTimestamp = Date.now().toString();
    localStorage.setItem("FORCE_LOGOUT", forceLogoutTimestamp);
    logger.error('AUTH', 'Force logout flag set');
    AuthService.logout();
  }
}
